<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import store from '@/store';
import { timezones } from '@/data/timezones';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import fileUpload from '@/shared/upload';
import { combineDateAndTime } from '@/shared/sticker-date-combos';

defineProps<{
  showErrors?: boolean;
  validationErrors?: string[];
}>();

dayjs.extend(utc);
dayjs.extend(timezone);

const { uploadStatus, handleUploadRequest } = fileUpload('stickers', 'Sticker');

const item = computed({
  get: () => store.getters['sticker/GET_STICKER'],
  set: (value) => {
    store.dispatch('sticker/SET_STICKER', value);
  },
});

const dateEvent = ref<string>('');
const timeEvent = ref<string>('');
const dateTimeTimezone = ref<string>('');


const locationTypeOptions = [
  { label: 'Physical Location', value: 'physical' },
  { label: 'Virtual Event', value: 'virtual' },
];

const combineTime = () => {
  if (!dateEvent.value || !timeEvent.value) return;
  const eventDate = combineDateAndTime(dateEvent.value, timeEvent.value);
  const targetTimeZone = dateTimeTimezone.value;

  if (targetTimeZone) {
    const localDate = dayjs.tz(eventDate, targetTimeZone);
    const eventDateInUTC = localDate.utc().format();
    store.dispatch('sticker/SET_STICKER', {
      eventDate: eventDateInUTC,
      eventDateTimeZone: targetTimeZone,
    });
  }
};

watch([dateEvent, timeEvent, dateTimeTimezone], () => {
  combineTime();
});

onMounted(() => {
  if (item.value.eventDate && item.value.eventDateTimeZone) {
    const tz = item.value.eventDateTimeZone;
    dateTimeTimezone.value = tz;
    const event = dayjs.utc(item.value.eventDate).tz(tz);
    dateEvent.value = event.format('YYYY-MM-DD');
    timeEvent.value = event.format('hh:mm A');
  }

  if (!item.value.locationType) {
    store.dispatch('sticker/SET_STICKER', { locationType: 'physical' });
  }
});

const bannerInput = ref<HTMLInputElement | null>(null);
const bannerUploading = ref(false);

const onBannerAreaClick = () => {
  bannerInput.value?.click();
};

const onBannerFileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;
  bannerUploading.value = true;
  try {
    await handleUploadRequest({ file });
    if (uploadStatus.status === 'ok') {
      store.dispatch('sticker/SET_STICKER', { bannerUrl: uploadStatus.resourceUrl });
    }
  } finally {
    bannerUploading.value = false;
    target.value = '';
  }
};

const onBannerDrop = async (event: DragEvent) => {
  event.preventDefault();
  const file = event.dataTransfer?.files?.[0];
  if (!file) return;
  bannerUploading.value = true;
  try {
    await handleUploadRequest({ file });
    if (uploadStatus.status === 'ok') {
      store.dispatch('sticker/SET_STICKER', { bannerUrl: uploadStatus.resourceUrl });
    }
  } finally {
    bannerUploading.value = false;
  }
};

const onBannerDragOver = (event: DragEvent) => {
  event.preventDefault();
};

const setLocationType = (val: string) => {
  store.dispatch('sticker/SET_STICKER', { locationType: val });
};

const setLocation = (e: Event) => {
  store.dispatch('sticker/SET_STICKER', { location: (e.target as HTMLInputElement).value });
};

const setVirtualLink = (e: Event) => {
  store.dispatch('sticker/SET_STICKER', { virtualLink: (e.target as HTMLInputElement).value });
};

const setHideLocation = (val: boolean) => {
  store.dispatch('sticker/SET_STICKER', { hideLocation: val });
};

</script>

<template lang="pug">
section.c-tab-event-basics
  .c-tab-section
    h4.c-tab-section__title Event Banner
    .c-banner-upload(
      @click="onBannerAreaClick"
      @drop="onBannerDrop"
      @dragover="onBannerDragOver"
      :class="{ 'c-banner-upload--has-image': item.bannerUrl }"
    )
      img.c-banner-upload__preview(v-if="item.bannerUrl" :src="item.bannerUrl" alt="Event Banner")
      template(v-else)
        .c-banner-upload__icon
          fai(type="fal" icon="cloud-upload-alt").size-32
        p.c-banner-upload__text(v-if="!bannerUploading") Drop banner image here or click to browse
        p.c-banner-upload__text(v-else) Uploading...
        p.c-banner-upload__hint Recommended: 1200x400px
      input(
        ref="bannerInput"
        type="file"
        accept="image/*"
        style="display: none"
        @change="onBannerFileChange"
      )

  .c-tab-section
    h4.c-tab-section__title
      fai(type="fal" icon="calendar-alt").c-tab-section__icon
      | Date &amp; Time
    .c-date-time-grid
      .c-date-time-field
        span.c-field-label Event Date*
        el-date-picker.c-date-picker(
          v-model="dateEvent"
          format="DD.MM.YYYY"
          placeholder="DD.MM.YYYY"
          popper-class="c-picker-popper"
          :style="{ '--el-input-height': '56px', height: '56px' }"
        )
      .c-date-time-field
        span.c-field-label Event Time*
        el-time-picker.c-custom-time(
          arrow-control
          format="hh:mm A"
          v-model="timeEvent"
          value-format="hh:mm A"
          popper-class="c-picker-popper"
        )
      .c-date-time-field
        span.c-field-label Timezone*
        el-select.c-tz-select(
          placeholder="Select timezone"
          v-model="dateTimeTimezone"
        )
          el-option(
            v-for="tz in timezones"
            :key="tz.value"
            :label="tz.label"
            :value="tz.value"
          )

  .c-tab-section
    h4.c-tab-section__title
      fai(type="fal" icon="map-marker-alt").c-tab-section__icon
      | Location
    .c-location-row
      .c-field
        span.c-field-label Location Type
        el-select.c-location-select(
          :model-value="item.locationType || 'physical'"
          @update:model-value="setLocationType"
        )
          el-option(
            v-for="opt in locationTypeOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          )
      .c-field(v-if="item.locationType !== 'virtual'")
        span.c-field-label Address*
        input.c-input(
          type="text"
          placeholder="123 Main St, City, State 12345"
          :value="item.location"
          @input="setLocation"
        )
      .c-field(v-else)
        span.c-field-label Virtual Link*
        input.c-input(
          type="url"
          placeholder="https://zoom.us/j/..."
          :value="item.virtualLink"
          @input="setVirtualLink"
        )
    .c-hide-location(v-if="item.locationType !== 'virtual'")
      el-switch(
        :model-value="item.hideLocation"
        @update:model-value="setHideLocation"
      )
      span.c-hide-location__label Hide location (for announcements)
</template>

<style scoped lang="scss">
.c-tab-event-basics {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.c-tab-section {
  padding: 20px;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  margin-bottom: 16px;
  background: #fff;

  &__title {
    font-size: 15px;
    font-weight: 700;
    font-family: inherit;
    color: #222;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__icon {
    color: #1ebeaf;
    font-size: 16px;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
}

.c-banner-upload {
  width: 100%;
  min-height: 160px;
  border: 2px dashed #ddd;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  gap: 8px;
  transition: border-color 0.2s;
  overflow: hidden;
  background: #fafafa;

  &:hover {
    border-color: #1ebeaf;
  }

  &--has-image {
    border-style: solid;
    border-color: #e0e0e0;
  }

  &__preview {
    width: 100%;
    height: 160px;
    object-fit: cover;
    display: block;
  }

  &__icon {
    color: #aaa;
  }

  &__text {
    font-size: 14px;
    color: #555;
    margin: 0;
    font-family: inherit;
  }

  &__hint {
    font-size: 12px;
    color: #aaa;
    margin: 0;
    font-family: inherit;
  }
}

.c-date-time-grid {
  display: flex;
  gap: 16px;

  @media (max-width: 768px) { flex-direction: column; }
}

.c-date-time-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  .c-date-picker {
    width: 100%;
    height: 56px;
    --el-input-height: 56px;
    --el-date-editor-width: 100%;

    :deep(.el-date-editor) {
      width: 100% !important;
      --el-date-editor-width: 100%;
    }

    :deep(.el-input__wrapper) {
      height: 56px;
      box-sizing: border-box;
      padding: 0 18px;
    }

    :deep(.el-input__inner) {
      height: 54px;
      line-height: 54px;
    }

    &.is-error {
      :deep(.el-input__wrapper) {
        box-shadow: 0 0 0 1px #f56c6c inset !important;
      }
    }
  }
}

.c-tz-select,
.c-location-select {
  width: 100%;

  :deep(.el-select__wrapper),
  :deep(.el-input__wrapper) {
    --el-input-height: 56px;
    height: 56px !important;
    box-sizing: border-box;
    padding: 0 18px;
  }

  &.is-error {
    :deep(.el-select__wrapper),
    :deep(.el-input__wrapper) {
      box-shadow: 0 0 0 1px #f56c6c inset !important;
    }
  }
}

.c-location-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    .c-field:first-child { flex: 1 1 auto; }
  }

  .c-field {
    margin-bottom: 0;
    min-width: 0;

    &:first-child {
      flex: 0 0 220px;
    }

    &:last-child {
      flex: 1;
    }
  }
}

.c-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.c-field-label {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  font-family: inherit;
}

.c-field-error {
  font-size: 12px;
  color: #f56c6c;
  margin-top: 2px;
  font-family: inherit;
}

.c-input {
  width: 100%;
  height: 56px;
  min-height: 56px;
  border: 1px solid var(--el-border-color);
  border-radius: 8px;
  padding: 0 18px;
  font-size: 14px;
  font-family: inherit;
  font-weight: 500;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:hover {
    border-color: var(--el-border-color-hover);
  }

  &:focus {
    border-color: var(--el-color-primary);
  }
}


.c-hide-location {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;

  &__label {
    font-size: 13px;
    color: #555;
    font-family: inherit;
  }
}
</style>

<style lang="scss">
.el-date-editor.c-date-picker.el-input,
.el-date-editor.c-custom-time.el-input {
  border-radius: 5px;
  color: #000;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  width: 100%;
  --el-date-editor-width: 100%;
  padding: 0;
  height: 56px;
  box-shadow: none;

  .el-input__wrapper {
    height: 56px;
    box-sizing: border-box;
    padding: 0 18px;
  }

  // _custom.scss floats the prefix and pads the text by 50px; keep both in the flow
  .el-input__prefix {
    position: static;
    padding-left: 0;
  }

  .el-input__icon {
    margin-right: 10px;
    font-size: 16px;
    color: #999;
  }

  .el-input__inner {
    padding: 0;
    font-size: 14px;
  }

  &.is-error .el-input__wrapper {
    box-shadow: 0 0 0 1px #f56c6c inset !important;
  }

  &:focus {
    outline: none;
  }
}

// Date/time dropdown: one card (popper and panel each drew their own border + shadow)
.el-popper.c-picker-popper {
  border: 1px solid #ececec;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  overflow: hidden;

  .el-popper__arrow {
    display: none;
  }

  .el-picker-panel,
  .el-time-panel {
    margin: 0;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  .el-time-panel {
    width: 200px;
  }

  .el-time-panel__content {
    padding: 4px 8px;

    // Selected row: a soft pill instead of two divider lines
    &::before {
      left: 8px;
      right: 8px;
      margin: -16px 0 0;
      padding: 0;
      border: none;
      border-radius: 8px;
      background: #effaf9;
    }

    &::after {
      display: none;
    }
  }

  .el-time-spinner__item {
    font-size: 13px;
    color: #888;

    &.is-active:not(.is-disabled) {
      color: #1ebeaf;
      font-weight: 600;
    }
  }

  .el-time-spinner__arrow {
    color: #aaa;

    &:hover {
      color: #1ebeaf;
    }
  }

  .el-time-panel__footer {
    height: auto;
    padding: 8px 12px;
    border-top: 1px solid #f0f0f0;
  }

  .el-time-panel__btn {
    margin: 0 0 0 4px;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    line-height: 1;

    &.cancel {
      color: #666;

      &:hover {
        background: #f5f5f5;
      }
    }

    &.confirm {
      color: #fff;
      background: #1ebeaf;
      font-weight: 600;

      &:hover {
        background: #19a89a;
      }
    }
  }
}
</style>
